/**
 * ローディング中に表示する「便利な使い方 Tips」の SSOT。
 *
 * 初回起動（index.html のプリローダー）／ルート遷移（Suspense fallback）／
 * データ取得・AI 生成中のローディング表示で共用する。
 *
 * ⚠️ 事実確認必須: 実在する機能・使い方のみを書くこと（存在しない機能を発明しない）。
 * 特に「パッと見では気づきにくい機能（hidden gems）」を優先的に紹介する方針。
 * 各項目は実装で確認済み（便利ツール `/tools`・イベント作成/詳細・公開ページ・通知設定等）。
 *
 * index.html のプリローダーにも1件インライン埋め込みしているため、トーンを揃えること
 * （`index.html` の `#kikakugoke-preloader` 参照）。
 */

/**
 * ローディング表示のタイミング定数（#205）。ルート遷移(Suspense)・各ページのデータ取得
 * ローダーで共通に使い、挙動を揃える。
 * - DELAY: この時間未満で読み込みが終わる局面ではヒントを一切出さない（チラつき防止）。
 * - MIN_VISIBLE: 一度ヒントを出したら、最低この時間は表示を維持する（読み切る前に消さない）。
 */
export const LOADING_TIP_DELAY_MS = 250;
export const LOADING_TIP_MIN_VISIBLE_MS = 2000;
/** 長時間待ち（AI 生成など）で Tip をローテーション切替する間隔(ms)。全箇所で共通に使う。 */
export const LOADING_TIP_ROTATE_INTERVAL_MS = 5000;

/** ローディング Tips 1件。`TipsSection` の `Tip` 型と互換の構造。 */
export interface LoadingTip {
  /** 見出し（短く） */
  label: string;
  /** 補足説明 */
  detail: string;
}

/**
 * Tips 一覧（50件）。ローディング中に必要なため lazy 化せず eager バンドルに同梱する。
 * 「知らないと気づけない」操作・機能を上位に厚めに配置している。
 */
export const LOADING_TIPS: LoadingTip[] = [
  // ── 隠れインタラクション ──
  {
    label: 'カード長押しでクイックメニュー',
    detail: 'スマホでダッシュボードや一覧のイベントカードを長押しすると、共有・複製・編集・削除などのメニューが開きます。',
  },
  {
    label: 'タップでその場編集',
    detail: 'イベント作成・編集の各項目は、鉛筆アイコンの行をタップするとその場で入力欄が開きます。',
  },
  {
    label: '日程はまとめて投票',
    detail: '日程調整の回答は「一括選択」から、すべての候補日をまとめて ○△× にできます。',
  },
  {
    label: '編集の破棄は確認される',
    detail: '設定シートを閉じるとき、変更があるときだけ「破棄しますか？」の確認が出ます。何もいじっていなければそのまま閉じます。',
  },
  {
    label: '前回の回答が自動で戻る',
    detail: '公開ページで一度回答すると、次にアクセスしたとき自分の投票・アンケート・コメントが復元されます。',
  },
  // ── 割り勘の隠れ機能 ──
  {
    label: '割り勘の負担を調整',
    detail: 'かんたん割り勘は、主賓を無料、後輩を固定額、幹事を×0.5〜×3の倍率にするなど一人ずつ負担を変えられます。',
  },
  {
    label: '割り勘の端数もスッキリ',
    detail: '割り勘は100円・1000円単位で丸めたり、割り切れない数円を特定の人にまとめたりできます。',
  },
  {
    label: '飲まない人を飲み物代から外す',
    detail: 'かんたん割り勘は支出ごとに「誰が負担するか」を選べるので、飲まない人を飲み物代から外せます。',
  },
  {
    label: '送金は最小回数に最適化',
    detail: 'かんたん割り勘は「誰が誰にいくら払うか」を、送金回数が最小になるよう自動で計算します。',
  },
  // ── 中間地点の隠れ機能 ──
  {
    label: '中間地点は2駅で自動計算',
    detail: 'みんなの中間地点は、最寄り駅を2つ以上入れるとボタンを押さなくても集合に便利な駅を探します。',
  },
  {
    label: '中間地点を路線で絞る',
    detail: 'みんなの中間地点は、新幹線・JR・地下鉄・大手私鉄などのカテゴリや、最低乗入路線数で候補駅を絞れます。',
  },
  {
    label: '公平重視で集合駅を選ぶ',
    detail: 'みんなの中間地点は「全員の移動時間の合計が最小」か「一番遠い人が最短」かを選べます。',
  },
  {
    label: '実際の乗車時間もすぐ確認',
    detail: 'みんなの中間地点の結果から、各参加者の実際の乗車時間を乗換案内ですぐ確認できます。',
  },
  // ── スポットシェア/ルート ──
  {
    label: 'スポットは☆で近い順に並ぶ',
    detail: 'スポットシェアで☆をタップすると基準地点になり、他のお店を近い順に自動で並べ替えます。',
  },
  {
    label: 'お店はURLを貼るだけ',
    detail: 'スポットシェアは Google Maps の共有リンクを貼るだけで候補地を追加できます。地図タップでも追加できます。',
  },
  {
    label: 'スポットにメモで比較',
    detail: 'スポットシェアは「個室あり」「予算5000円」などのメモを付けて、お店を並べて比べられます。',
  },
  {
    label: '経由地ルートは区間ごとに比較',
    detail: '経由地ルートは各区間で時間優先・運賃優先・乗換少を切り替え、最大5候補から選べます。',
  },
  {
    label: '経由地の入れ替えで自動再計算',
    detail: '経由地ルートは経由地を並べ替えると、全体のルートを自動で計算し直します。',
  },
  // ── アイデア帳/抽選 ──
  {
    label: 'アイデア帳は9つの軸で絞り込み',
    detail: 'イベントアイデア帳は、人数・予算・テンション・入りやすさ・レア度など9つの軸で200以上のネタを検索できます。',
  },
  {
    label: '迷ったら「おまかせ」',
    detail: 'イベントアイデア帳は条件を決めてから「おまかせ」にすると、条件に合うネタをランダムに提案します。',
  },
  {
    label: '抽選は3モード',
    detail: 'ルーレットは「1名抽選」「あみだくじ（全員に割り当て）」「シャッフル（順番並べ替え）」を切り替えられます。',
  },
  {
    label: '当たりやすさに重み付け',
    detail: 'ルーレットは参加者ごとに重みを設定して、当たりやすさに傾斜をつけられます。',
  },
  {
    label: '名前はまとめて入力',
    detail: 'ルーレットは「太郎、花子、次郎」と区切って複数人を一度に追加できます（重複は自動でスキップ）。',
  },
  // ── タイムテーブル/買い出し/持ち物 ──
  {
    label: 'タイムテーブルは今をハイライト',
    detail: 'タイムテーブルは当日に開くと、今の時間帯が自動で強調されて「次は何時から？」がひと目で分かります。',
  },
  {
    label: '進行表はテンプレから',
    detail: 'タイムテーブルは BBQ・日帰り旅行・飲み会・会議のテンプレから作り始められます。',
  },
  {
    label: '買い出しは担当で絞り込み',
    detail: '買い出しリストは担当者を設定すると、自分の担当品だけを絞り込んで表示できます。',
  },
  // ── イベント統合ツール ──
  {
    label: '担当はチップで割り当て',
    detail: 'イベントの持ち物・買い出し・やることは、参加者名のチップから担当をワンタップで割り当てられます。',
  },
  {
    label: 'ツールはイベントに保存できる',
    detail: '割り勘やタイムテーブルなどはイベントに紐付けて保存でき、参加者と一緒に共同編集できます。',
  },
  {
    label: 'ツールをURLで書き出し',
    detail: 'イベント内のツールはスタンドアロンのURLに書き出したり、逆にURLから取り込んだりできます。',
  },
  // ── 一覧/複製/タイムライン ──
  {
    label: '複製で日程をずらす',
    detail: 'イベントを複製するとき、日時を「翌日・1週間後・1ヶ月後」へずらしたり、下書きとして複製できます。',
  },
  {
    label: 'タイムラインで年月ジャンプ',
    detail: 'イベントのタイムライン表示は、右端の目次から年月をタップしてその位置へ移動できます。',
  },
  {
    label: '一覧は自由に並び替え・絞り込み',
    detail: 'イベント一覧は日時・作成日・タイトル・参加者数で並び替え、主催/参加中・ステータス・タグで絞り込めます。',
  },
  {
    label: 'タグは過去から候補提示',
    detail: 'タグを入力すると、過去のイベントでよく使ったタグが候補として出てきます。',
  },
  {
    label: 'タイトルも過去から候補提示',
    detail: 'イベントのタイトル入力でも、過去に作ったイベント名が入力候補として出てきます。',
  },
  {
    label: 'テンプレートギャラリー',
    detail: '季節のおすすめや通年のテンプレを選ぶと、イベント作成フォームにプリセットが反映されて素早く作れます。',
  },
  // ── 公開ページ/カレンダー/祝日 ──
  {
    label: 'カレンダーにワンタップ追加',
    detail: '公開ページから Google・Outlook・iPhone のカレンダーにイベントを追加できます。',
  },
  {
    label: '祝日がひと目で分かる',
    detail: '日程を選ぶカレンダーは、日本の祝日を強調表示します。',
  },
  {
    label: 'コメントは編集・削除できる',
    detail: 'イベントのコメントは後から編集でき、編集すると「編集済み」が付きます。削除もできます。',
  },
  // ── アンケート ──
  {
    label: 'アンケートはテンプレから',
    detail: 'アンケートは「テンプレートから追加」で、飲み会・BBQ・旅行などの定番質問をまとめて追加できます。',
  },
  {
    label: 'アンケートは4タイプ',
    detail: 'アンケートは単一選択・複数選択・自由記述に加えて、参加者の最寄り駅を集める質問も作れます。',
  },
  {
    label: '最寄駅アンケート→中間地点',
    detail: '最寄駅アンケートで集めた駅は、そのまま「みんなの中間地点」で集合駅の計算に使えます。',
  },
  // ── 通知 ──
  {
    label: 'リマインドは複数タイミング',
    detail: 'リマインド通知は前日・当日など、複数のタイミングをまとめて設定できます。',
  },
  {
    label: '通知メールは送信前プレビュー',
    detail: '参加者に届く通知メールの件名・本文を、送信前にプレビューで確認できます。',
  },
  {
    label: '通知はチャネルと種別を選べる',
    detail: '通知の受け取り方はメール／LINE／オフを選べ、参加・変更・中止など種別ごとにオプトアウトできます。',
  },
  {
    label: 'LINEで通知を受け取る',
    detail: '有料プランでは、LINE公式アカウントと連携して参加状況の更新などを LINE で受け取れます。',
  },
  // ── 有料/詳細設定 ──
  {
    label: '公開ページの見た目を変える',
    detail: '有料プランでは、公開ページのカラーテーマとヘッダー模様を選んで印象を変えられます。',
  },
  {
    label: '定員・ゲスト可否など詳細設定',
    detail: '高度な設定で、定員（超過は受付停止）・ゲスト参加の可否・「保留(△)」の無効化などを設定できます。',
  },
  // ── コア機能 ──
  {
    label: 'AIが企画のたたき台',
    detail: '日時・人数・やりたいことの3つに答えるだけで、AIがイベント企画のたたき台を作ります。',
  },
  {
    label: 'ゲストはログイン不要',
    detail: '参加者はアカウント登録なしで、共有リンクから出欠を登録できます。',
  },
  {
    label: '共有文は自分で編集できる',
    detail: 'LINE・X・メールで送る共有メッセージは自分で編集でき、未入力なら気の利いた既定文が使われます。',
  },
  {
    label: '待ち時間に苔ミニゲーム',
    detail: '便利ツールには苔キャラのミニゲームが5種（天運・採集・聳立・記憶・消散）。待ち時間の暇つぶしにどうぞ。',
  },
];

/**
 * ランダムに Tip のインデックスを返す。
 * `Math.random` はアプリコードでは使用可（メモリー参照）。
 * `useState` の遅延初期化などレンダー外で呼ぶこと。
 */
export function pickRandomTipIndex(count: number = LOADING_TIPS.length): number {
  return Math.floor(Math.random() * count);
}

/**
 * 連続したローディング（ルート遷移 → 認証確認 → データ取得 など、複数のローダーが
 * 短時間に入れ替わり表示される）で、毎回ランダムに Tip が変わって見える問題を防ぐ。
 *
 * 直近 `TIP_STABLE_GAP_MS` 以内に別のローダーが Tip を表示していたら**同じ Tip を使い回す**。
 * 十分に間が空いた（＝別のローディング機会）ときだけ新しい Tip を選ぶ。これにより、一連の
 * 初回ロードでは基本的に1つの Tip だけが表示される。
 */
declare global {
  interface Window {
    /** index.html のプリローダーが選んだ Tip。React 側が初回だけ引き継いで画面全体で1つに見せる。 */
    __KIKAKUGOKE_PRELOADER_TIP__?: { label: string; detail: string };
  }
}

const TIP_STABLE_GAP_MS = 800;
let cachedTip: LoadingTip = LOADING_TIPS[0];
/** アプリ起動後、まだ一度も「新規の Tip」を選んでいないか（初回はプリローダー Tip を引き継ぐ）。 */
let isFirstPick = true;
/** 現在マウント中の LoadingTips 数。>0 の間は「表示継続中」とみなす。 */
let activeLoadingTips = 0;
/** 最後にローダーが活性だった時刻。ローダーが一瞬途切れても GAP 内なら継続扱いにする。 */
let lastActiveAt = Number.NEGATIVE_INFINITY;

export interface AcquiredTip {
  tip: LoadingTip;
  /** 直前まで別ローダーが表示していた（＝連続表示）か。true なら遅延なしで即表示してよい。 */
  continuation: boolean;
}

/** index.html のプリローダーが window に残した Tip を読む（初回引き継ぎ用）。無ければ null。 */
function readPreloaderTip(): LoadingTip | null {
  const pre = typeof window !== 'undefined' ? window.__KIKAKUGOKE_PRELOADER_TIP__ : undefined;
  if (pre && typeof pre.label === 'string' && typeof pre.detail === 'string') {
    return { label: pre.label, detail: pre.detail };
  }
  return null;
}

/** 新規に表示する Tip を1つ選ぶ。アプリ起動後の初回はプリローダー Tip を引き継ぐ。 */
function pickFreshTip(): LoadingTip {
  if (isFirstPick) {
    isFirstPick = false;
    const preloaderTip = readPreloaderTip();
    if (preloaderTip) return preloaderTip;
  }
  return LOADING_TIPS[pickRandomTipIndex()];
}

/**
 * ローダー表示時（レンダー中の useState 初期化）に呼ぶ。
 * 他のローダーが現在表示中、または直近 `TIP_STABLE_GAP_MS` 以内に表示されていたら
 * **同じ Tip を使い回す**（＝連続ロードでは Tip を切り替えない）。
 * アプリ起動後の初回は index.html のプリローダーが表示していた Tip を引き継ぐ
 * （フルリロード時にプリローダー → React で Tip が入れ替わって見えるのを防ぐ）。
 */
export function acquireLoadingTip(): AcquiredTip {
  const continuation =
    activeLoadingTips > 0 || performance.now() - lastActiveAt <= TIP_STABLE_GAP_MS;
  if (!continuation) {
    cachedTip = pickFreshTip();
  }
  return { tip: cachedTip, continuation };
}

/**
 * ローテーション（長時間待ちで一定間隔ごとに Tip を切替）で次の Tip を選ぶ。
 * 現在の共有 Tip（cachedTip）と異なるものを選び、**共有 Tip も更新**する。
 * これにより、回転後に別ローダーが継続表示しても同じ（回転後の）Tip に揃う。
 */
export function rotateSharedTip(): LoadingTip {
  if (LOADING_TIPS.length <= 1) return cachedTip;
  let next = cachedTip;
  while (next === cachedTip) {
    next = LOADING_TIPS[pickRandomTipIndex()];
  }
  cachedTip = next;
  return next;
}

/** LoadingTips のマウント時に呼ぶ（活性カウント++）。 */
export function retainLoadingTip(): void {
  activeLoadingTips += 1;
  lastActiveAt = performance.now();
}

/** LoadingTips のアンマウント時に呼ぶ（活性カウント--）。 */
export function releaseLoadingTip(): void {
  activeLoadingTips = Math.max(0, activeLoadingTips - 1);
  lastActiveAt = performance.now();
  // ヒントが全て消えた（＝1つのローディング機会が終了）→ 最低表示時間の起点をリセット。
  if (activeLoadingTips === 0) {
    hintVisibleSince = null;
  }
}

// ── ヒントの「最低表示時間」を複数ローダー間で共有する起点 ──
// オーバーレイ（NavigationLoading）と遷移先のデータローダー（useHeldLoading）はそれぞれ独立に
// 最低表示時間を数えるため、放置すると 2秒 + 2秒 で最大4秒近く表示され得る。1回のローディング
// 機会で「ヒントが最初に見えた時刻」を共有し、どのローダーもそこから最低時間で揃えることで、
// 合計を最低表示時間ちょうどに収める。
let hintVisibleSince: number | null = null;

/** LoadingTips が実際にヒントを表示した瞬間に呼ぶ。セッションの起点を1度だけ記録する。 */
export function noteHintVisible(): void {
  if (hintVisibleSince === null) {
    hintVisibleSince = performance.now();
  }
}

/**
 * ヒントを消してよくなるまでの残り時間(ms)。ヒント表示開始（`hintVisibleSince`）から
 * `minVisibleMs` 経過するまでは正の値を返す。まだ一度も表示していなければ全時間を返す。
 */
export function hintHoldRemaining(minVisibleMs: number): number {
  if (hintVisibleSince === null) return minVisibleMs;
  return Math.max(0, minVisibleMs - (performance.now() - hintVisibleSince));
}
